import { TextListCard } from '@atlas/ui-domain'
import { useBrandMessages } from '@/hooks/useExplore'
import { BRAND_COPY_WORDS } from './brand-page-words'
import { toTextListItems } from './text-list-adapter'

export interface BrandCopyTabProps {
	brandCode: string
	/** The codes of the brand's products, whose copy is part of the brand's matrix. */
	productCodes: string[]
	/** The picked locale, held by the page: an inactive tab panel is unmounted, so holding
	 * the pick here would lose it to a look at another tab. */
	locale: string | null
	onLocaleChange: (locale: string | null) => void
}

/**
 * The brand's message matrix: the copy classified to the brand itself and to every product
 * under it, read per locale.
 *
 * Unnarrowed, like the assets tab - the page cannot judge what applies in its market without an
 * applicability ladder, so it states the whole matrix rather than a guess at part of it.
 *
 * The picked locale is the page's rather than the card's because it survives the card's own
 * re-derivation: `localeInForce` honours the pick again if a narrowing brings that locale back.
 * It is held above this tab rather than in it so that it also survives a tab switch, which
 * unmounts the panel.
 */
export function BrandCopyTab({ brandCode, productCodes, locale, onLocaleChange }: BrandCopyTabProps) {
	const { data: messages = [], isLoading, isError } = useBrandMessages(brandCode, productCodes)

	return (
		<TextListCard
			texts={toTextListItems(messages)}
			locale={locale}
			onLocaleChange={onLocaleChange}
			isLoading={isLoading}
			isError={isError}
			emptyLabel={BRAND_COPY_WORDS.emptyLabel}
		/>
	)
}
